import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { Button, IconButton, Stack, TableCell, TableRow, Theme, Tooltip, Typography } from '@mui/material';
import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import {
    SextforceAutoCampaignTipReply,
    SextforceAutoCampaignTipReplyMessageDetails,
    SextforceAutoCampaignTipReplyWithMessageDetails,
} from '../../../../hooks/useSextforceAutoCampaignTipReply';
import { SettingsContext } from '../../../../store/SettingsContext';
import { UserContext } from '../../../../store/UserContext';
import { handleHttpError } from '../../../../utils/common';
import SextforceAutoCampaignTipReplyMessageDetailsDialog from './SextforceAutoCampaignTipReplyMessageDetailsDialog';
import SextforceAutoCampaignTipReplyTippersListDialog from './SextforceAutoCampaignTipReplyTippersListDialog';

type Props = {
    subscriber: any;
    autoCampaignTipReply: SextforceAutoCampaignTipReplyWithMessageDetails;
    tipOption: number;
    theme: Theme;
    createAutoCampaignTipReplyTipMessageDetails: (
        autoCampaignTipReply: SextforceAutoCampaignTipReply,
        autoCampaignTipReplyTipMessageDetails: SextforceAutoCampaignTipReplyMessageDetails,
    ) => Promise<SextforceAutoCampaignTipReplyMessageDetails | null>;
    updateAutoCampaignTipReplyTipMessageDetails: (
        autoCampaignTipReply: SextforceAutoCampaignTipReply,
        autoCampaignTipReplyTipMessageDetails: SextforceAutoCampaignTipReplyMessageDetails,
    ) => Promise<SextforceAutoCampaignTipReplyMessageDetails | null>;
    deleteAutoCampaignTipReplyTipMessageDetails: (
        autoCampaignTipReply: SextforceAutoCampaignTipReply,
        autoCampaignTipReplyTipMessageDetails: SextforceAutoCampaignTipReplyMessageDetails,
    ) => Promise<SextforceAutoCampaignTipReplyMessageDetails | null>;
    refetchAutoCampaignTipReplies: () => void;
};

const SextforceAutoCampaignTipReplyTableTipRow = (props: Props) => {
    const {
        subscriber,
        autoCampaignTipReply,
        tipOption,
        theme,
        refetchAutoCampaignTipReplies,
        createAutoCampaignTipReplyTipMessageDetails,
        updateAutoCampaignTipReplyTipMessageDetails,
        deleteAutoCampaignTipReplyTipMessageDetails,
    } = props;
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();

    const [newMessageDetailsDialogOpen, setNewMessageDetailsDialogOpen] = useState(false);
    const [editingMessageDetails, setEditingMessageDetails] = useState<SextforceAutoCampaignTipReplyMessageDetails | null>(null);
    const [tippersListDialogOpen, setTippersListDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Tip amount formatted string
    const tipAmountFormatted = `$${tipOption.toLocaleString(undefined, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

    // All the reply variants for this tip amount ("spinning wheel"), sorted by creation order (oldest first)
    const replyVariants = autoCampaignTipReply.messageDetails
        .filter(messageDetail => messageDetail.tipAmount === tipOption)
        .sort((a, b) => (a._id || '').localeCompare(b._id || ''));

    // Total number of replies sent for this tip amount across all the variants
    const totalReplyCount = replyVariants.reduce((total, messageDetail) => total + (messageDetail.replyCount || 0), 0);

    // Reply variant label - the variant's name or "Reply N"
    const variantLabel = (messageDetails: SextforceAutoCampaignTipReplyMessageDetails, index: number) =>
        messageDetails.name && messageDetails.name.trim().length > 0 ? messageDetails.name : `Reply ${index + 1}`;

    // Delte Auto Campaign Tip Reply
    const handleDelete = async (messageDetails: SextforceAutoCampaignTipReplyMessageDetails) => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return;
        }

        setIsDeleting(true);

        const data = await deleteAutoCampaignTipReplyTipMessageDetails(autoCampaignTipReply, messageDetails)
            .then(async response => {
                setIsDeleting(false);

                return response;
            })
            .catch(error => {
                setIsDeleting(false);
                handleHttpError(error, dialog);

                return null;
            });

        return data;
    };

    const handleDeleteAutoCampaignTipReply = async (messageDetails: SextforceAutoCampaignTipReplyMessageDetails) => {
        handleDelete(messageDetails).then(data => {
            if (!data) {
                return;
            }

            refetchAutoCampaignTipReplies();
        });
    };

    return (
        <>
            <TableRow
                sx={{
                    backgroundColor: theme.palette.grey[50],
                    ...(replyVariants.length > 0
                        ? {
                              '& td': {
                                  borderBottom: 'none',
                              },
                          }
                        : {}),
                }}
            >
                <TableCell>
                    <Stack direction={'row'} spacing={0.5}>
                        <AttachMoneyIcon fontSize="small" />
                        <Typography variant="inherit" color="textPrimary" noWrap>
                            {tipAmountFormatted}
                        </Typography>
                    </Stack>
                </TableCell>
                <TableCell align="center">{totalReplyCount.toLocaleString()}</TableCell>
                <TableCell>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        {replyVariants.length === 0 ? (
                            <Tooltip title={`Create Reply for Tip Amount ${tipAmountFormatted}`}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={!autoCampaignTipReply.active}
                                    onClick={e => {
                                        e.stopPropagation();
                                        setNewMessageDetailsDialogOpen(true);
                                    }}
                                >
                                    Create
                                </Button>
                            </Tooltip>
                        ) : (
                            <>
                                <Tooltip
                                    title={`Add Another Reply for Tip Amount ${tipAmountFormatted}. Multiple replies rotate per fan like a spinning wheel - a fan who tips this amount again will receive a different reply until all replies have been used.`}
                                >
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={!autoCampaignTipReply.active}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setNewMessageDetailsDialogOpen(true);
                                            }}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Tooltip title="List of Tippers">
                                    <IconButton
                                        size="small"
                                        color="info"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setTippersListDialogOpen(true);
                                        }}
                                    >
                                        <PeopleAltIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {tippersListDialogOpen && (
                                    <SextforceAutoCampaignTipReplyTippersListDialog
                                        subscriber={subscriber}
                                        autoCampaignTipReply={autoCampaignTipReply}
                                        messageDetailsList={autoCampaignTipReply.messageDetails}
                                        amount={tipOption}
                                        open={tippersListDialogOpen}
                                        onClose={() => setTippersListDialogOpen(false)}
                                        theme={theme}
                                    />
                                )}
                            </>
                        )}

                        {newMessageDetailsDialogOpen && (
                            <SextforceAutoCampaignTipReplyMessageDetailsDialog
                                subscriber={subscriber}
                                autoCampaignTipReply={autoCampaignTipReply}
                                initialValues={null}
                                tipOption={tipOption}
                                theme={theme}
                                open={newMessageDetailsDialogOpen}
                                onClose={() => {
                                    setNewMessageDetailsDialogOpen(false);
                                }}
                                refetchAutoCampaignTipReplies={refetchAutoCampaignTipReplies}
                                createAutoCampaignTipReplyTipMessageDetails={createAutoCampaignTipReplyTipMessageDetails}
                                updateAutoCampaignTipReplyTipMessageDetails={updateAutoCampaignTipReplyTipMessageDetails}
                            />
                        )}
                    </Stack>
                </TableCell>
            </TableRow>
            {replyVariants.map((messageDetails, index) => (
                <TableRow
                    key={messageDetails._id || index}
                    sx={
                        index < replyVariants.length - 1
                            ? {
                                  '& td': {
                                      borderBottom: 'none',
                                  },
                              }
                            : undefined
                    }
                >
                    <TableCell>
                        <Stack direction={'row'} spacing={0.5} alignItems="center" sx={{ pl: 2 }}>
                            <SubdirectoryArrowRightIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                            <Typography variant="inherit" color="textSecondary" noWrap>
                                {variantLabel(messageDetails, index)}
                            </Typography>
                        </Stack>
                    </TableCell>
                    <TableCell align="center">
                        <Typography variant="inherit" color="textSecondary">
                            {(messageDetails.replyCount || 0).toLocaleString()}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title={`Edit ${variantLabel(messageDetails, index)} for Tip Amount ${tipAmountFormatted}`}>
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={!autoCampaignTipReply.active}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setEditingMessageDetails(messageDetails);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title={`Delete ${variantLabel(messageDetails, index)} for Tip Amount ${tipAmountFormatted}`}>
                                <span>
                                    <IconButton
                                        color="error"
                                        size="small"
                                        disabled={!autoCampaignTipReply.active || isDeleting}
                                        onClick={e => {
                                            e.stopPropagation();

                                            dialog
                                                .confirm({
                                                    title: `Delete Tip Campaign Reply`,
                                                    message: `Are you sure you want to delete ${variantLabel(
                                                        messageDetails,
                                                        index,
                                                    )} for tip amount ${tipAmountFormatted}?`,
                                                    cancel: {
                                                        text: 'Cancel',
                                                        variant: 'outlined',
                                                    },
                                                    ok: {
                                                        text: 'Delete',
                                                        variant: 'contained',
                                                        color: 'error',
                                                    },
                                                })
                                                .then(() => {
                                                    if (autoCampaignTipReply._id) {
                                                        handleDeleteAutoCampaignTipReply(messageDetails);
                                                    }
                                                })
                                                .catch(() => {});
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </TableCell>
                </TableRow>
            ))}
            {editingMessageDetails && (
                <SextforceAutoCampaignTipReplyMessageDetailsDialog
                    subscriber={subscriber}
                    autoCampaignTipReply={autoCampaignTipReply}
                    initialValues={editingMessageDetails}
                    tipOption={tipOption}
                    theme={theme}
                    open={true}
                    onClose={() => {
                        setEditingMessageDetails(null);
                    }}
                    refetchAutoCampaignTipReplies={refetchAutoCampaignTipReplies}
                    createAutoCampaignTipReplyTipMessageDetails={createAutoCampaignTipReplyTipMessageDetails}
                    updateAutoCampaignTipReplyTipMessageDetails={updateAutoCampaignTipReplyTipMessageDetails}
                />
            )}
        </>
    );
};

export default SextforceAutoCampaignTipReplyTableTipRow;
